/**
 * "InMail schreiben": put the draft on the clipboard and open the recruiter's own LinkedIn
 * Recruiter composer in a new tab. Nothing is sent — the recruiter pastes and sends in LinkedIn.
 *
 * Verified Oct 12 2026: `/talent/profile/<memberId>?rightRail=composer` opens the composer with the
 * recipient set; the vanity slug does not work there, and the URL cannot prefill the text. Without
 * a member id we open the public profile, from which "View in Recruiter" and the envelope are two
 * more clicks.
 *
 * Order matters (same rule as `TicketDetailModal.handleMessageOnSlack`): the clipboard write first,
 * while the click still owns focus, then `window.open` — no network call in between, or the
 * browser treats the open as a pop-up.
 */
type Composable = { recruiterId: string | null; publicUrl: string | null; recruiterProjectId?: string | null };

/** The project id the proxy sends, if it is one (digits, as `recruiterProjectId` is validated there). */
const projectOf = (c: Composable) => (c.recruiterProjectId && /^\d{5,20}$/.test(c.recruiterProjectId) ? c.recruiterProjectId : null);

/**
 * Inside the role's Recruiter project when the proxy knows it (Sofia, Oct 27 2026): only an InMail
 * sent from inside the project moves the person to "Contacted" there; one sent from the plain
 * profile does not (manual test, Oct 27). The shape is the one copied from a live tab. `inProject:
 * false` keeps the plain composer — for an extension older than 0.8.0, which opens nothing else.
 */
export function composerUrl(c: Composable, { inProject = true }: { inProject?: boolean } = {}): string | null {
  if (c.recruiterId) {
    const id = encodeURIComponent(c.recruiterId);
    const project = inProject ? projectOf(c) : null;
    if (project) {
      const p = encodeURIComponent(project);
      return `https://www.linkedin.com/talent/hire/${p}/manage/all/profile/${id}/messages?project=${p}&rightRail=composer`;
    }
    return `https://www.linkedin.com/talent/profile/${id}?rightRail=composer`;
  }
  return c.publicUrl || null;
}

/** Whether a send to them happens inside a project, so LinkedIn sets "Contacted" (Oct 27 2026). */
export const sendsInProject = (c: Composable) => Boolean(c.recruiterId && projectOf(c));

const LINK = /\[([^\]\n]+)\]\(((?:https?:\/\/|mailto:)[^)\s]+)\)/g;

/**
 * Subject on the first line, a blank line, then the body — what pastes cleanly into the composer.
 * A `[words](https://…)` link reads "words (https://…)" as plain text (Oct 22 2026).
 */
export const clipboardText = (subject: string, body: string) => {
  const plain = `${body || ''}`.replace(LINK, '$1 ($2)');
  return subject ? `${subject}\n\n${plain}` : plain;
};

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
/** The same as HTML, so a paste into LinkedIn's rich composer keeps the links as links. */
export const clipboardHtml = (subject: string, body: string) =>
  clipboardText(subject, '')
    .split('\n')
    .filter(Boolean)
    .map((l) => `<p>${esc(l)}</p>`)
    .join('') +
  `${body || ''}`
    .split(/\n{2,}/)
    .map((para) => {
      let out = '';
      let last = 0;
      for (const m of para.matchAll(LINK)) {
        out += esc(para.slice(last, m.index)).replace(/\n/g, '<br>') + `<a href="${esc(m[2])}">${esc(m[1])}</a>`;
        last = (m.index ?? 0) + m[0].length;
      }
      return `<p>${out}${esc(para.slice(last)).replace(/\n/g, '<br>')}</p>`;
    })
    .join('');

export async function copyAndOpenComposer(
  c: Composable,
  subject: string,
  body: string
): Promise<{ copied: boolean; opened: boolean; direct: boolean }> {
  let copied = false;
  try {
    const text = clipboardText(subject, body);
    const Item = (globalThis as { ClipboardItem?: typeof ClipboardItem }).ClipboardItem;
    let rich = false;
    if (/\[[^\]\n]+\]\((?:https?:\/\/|mailto:)[^)\s]+\)/.test(body) && Item && navigator.clipboard.write) {
      // Rich copy where the browser allows it; plain text otherwise (CodeRabbit, PR #681).
      try {
        await navigator.clipboard.write([
          new Item({
            'text/plain': new Blob([text], { type: 'text/plain' }),
            'text/html': new Blob([clipboardHtml(subject, body)], { type: 'text/html' }),
          }),
        ]);
        rich = true;
      } catch {
        rich = false;
      }
    }
    if (!rich) await navigator.clipboard.writeText(text);
    copied = true;
  } catch {
    copied = false;
  }
  const url = composerUrl(c);
  if (!url) return { copied, opened: false, direct: false };
  // Not `window.open(url, '_blank', 'noopener')`: with 'noopener' the call returns null even when
  // the tab DID open, so a blocked pop-up could not be told apart. Open blank, cut the opener, then
  // navigate (the TicketDetailModal pattern).
  const tab = window.open('', '_blank');
  if (!tab) return { copied, opened: false, direct: Boolean(c.recruiterId) };
  tab.opener = null;
  tab.location.href = url;
  return { copied, opened: true, direct: Boolean(c.recruiterId) };
}
